import { HttpMethodEnum } from '@ipevo-rd/service-web-utils'
import { adminHttpClient } from './client.js'
import { getViewerBaseUrl } from '../../api.js'
import type {
	AdminChannelId,
	ChannelInfo,
	ChannelHistory,
	channelHistoryCell,
} from './dto.js'

/**
 * 取得全部頻道清單（/v1/admin/channels，admin 用）
 * @returns 頻道 id 清單
 */
export const getAllChannels = async (): Promise<
	AdminChannelId[] | undefined
> => {
	// 取得 admin 視角的全部頻道
	const body = await adminHttpClient.request<AdminChannelId[]>({
		url: '/admin/channels',
		method: HttpMethodEnum.GET,
	})

	// 回傳頻道 id 清單
	return body.data
}

/**
 * 依 channel id 或短碼取得頻道完整資訊（/v1/admin/channels）
 * @param channelId 頻道 id（可選）
 * @param shortUrl 頻道短碼（可選）
 * @returns 頻道完整資訊
 */
export const getChannelInfo = async (
	channelId?: string | null,
	shortUrl?: string | null,
): Promise<ChannelInfo | undefined> => {
	// 組合查詢參數（id 或 code 擇一）
	const params = new URLSearchParams()

	// 如果有 channelId 就帶上 id 參數
	if (channelId) {
		params.append('id', channelId)
	}

	// 如果有 shortUrl 就帶上 code 參數
	if (shortUrl) {
		params.append('code', shortUrl)
	}

	// 發送 GET 請求到 /v1/admin/channels，帶上查詢參數
	const body = await adminHttpClient.request<ChannelInfo>({
		url: `/admin/channels?${params.toString()}`,
		method: HttpMethodEnum.GET,
	})

	// 回傳頻道完整資訊
	return body.data
}

/**
 * 取得頻道歷史訊息（/v1/admin/channels/histories）
 * @param channelId 頻道 id
 * @returns 歷史訊息陣列
 */
export const getHistory = async (
	channelId: string,
): Promise<channelHistoryCell[]> => {
	// 組合查詢參數
	const params = new URLSearchParams()

	// 帶上頻道 id
	params.append('id', channelId)

	// 發送 GET 請求到 /v1/admin/channels/histories，帶上查詢參數
	const body = await adminHttpClient.request<ChannelHistory>({
		url: `/admin/channels/histories?${params.toString()}`,
		method: HttpMethodEnum.GET,
	})

	// 回傳歷史訊息陣列
	return body.data.history
}

/**
 * 觸發後端重新產生頻道摘要（POST /v1/admin/channels/summary，admin 不驗所有權）
 * @param channelId 頻道 id
 */
export const generateChannelSummary = async (
	channelId: string,
): Promise<void> => {
	// 帶 channel_id 打 admin 摘要重生端點；與 tts / delete / clear / edit 同款 body 形式
	await adminHttpClient.request({
		url: '/admin/channels/summary',
		method: HttpMethodEnum.POST,
		data: { channel_id: channelId },
	})
}

/**
 * 切換頻道 TTS 開關（PUT /v1/admin/channels/tts）
 * @param channelId 頻道 id
 * @param isTtsActive 是否開啟 TTS
 */
export const setChannelTTS = async (
	channelId: string,
	isTtsActive: boolean,
): Promise<void> => {
	// 帶 channel_id + is_tts_active 打 TTS 開關端點
	await adminHttpClient.request({
		url: '/admin/channels/tts',
		method: HttpMethodEnum.PUT,
		data: { channel_id: channelId, is_tts_active: isTtsActive },
	})
}

/**
 * 永久刪除頻道（DELETE /v1/admin/channels/delete）
 * @param channelId 頻道 id
 */
export const deleteChannel = async (channelId: string): Promise<void> => {
	// 帶 channel_id 打刪除端點
	await adminHttpClient.request({
		url: '/admin/channels/delete',
		method: HttpMethodEnum.DELETE,
		data: { channel_id: channelId },
	})
}

/**
 * 清空頻道 History & Audio 資源（POST /v1/admin/channels/clear）
 * @param channelId 頻道 id
 */
export const clearChannel = async (channelId: string): Promise<void> => {
	// 帶 channel_id 打清空端點
	await adminHttpClient.request({
		url: '/admin/channels/clear',
		method: HttpMethodEnum.POST,
		data: { channel_id: channelId },
	})
}

/**
 * 頻道編輯可變更欄位（channel_id 另外帶，這裡只放有改才送的欄位）
 */
export interface EditChannelPayload {
	/** 頻道標題 */
	title?: string
	/** 翻譯目標語言清單 */
	languages?: string[]
	/** 觀眾容量上限 */
	capacity?: number
}

/**
 * 編輯頻道（PUT /v1/admin/channels/edit）
 * @param channelId 頻道 id
 * @param payload 有變更的欄位（title / languages / capacity）
 */
export const editChannel = async (
	channelId: string,
	payload: EditChannelPayload,
): Promise<void> => {
	// channel_id 必帶，其餘欄位由 caller 篩選後展開
	await adminHttpClient.request({
		url: '/admin/channels/edit',
		method: HttpMethodEnum.PUT,
		data: { channel_id: channelId, ...payload },
	})
}

/**
 * 連接 admin 監看 SSE（GET /v1/channels/{id}/admin/events?token=<MEB_TOKEN>）
 * - 一條連線同時收翻譯訊息 + 系統訊息 + 在線人數；caller 自行 addEventListener('notify') 後讀 JSON 的 sse_type 分流
 * - admin 連線不計入觀眾人數 / 統計，純監看，故不需要 viewer_id / renew
 * - token 走 query string（EventSource 無法帶自訂 header）
 * - base 走 getViewerBaseUrl()（與觀眾 SSE 同網域，經 nchan proxy 到 viewer service）
 * @param channelId 要監看的頻道 id
 * @param token 必須是帶 tfa=true 的 admin 正式 token
 * - 暫時 token 會被伺服器回 403
 * - 伺服器每 5 分鐘重新驗證一次，拒絕就關閉串流，暫時故障容忍一次
 * - 單條連線最長 1 小時，到時 EventSource 會自動重連
 * @returns 已建立連線的 EventSource
 */
export const connectAdminEvents = (
	channelId: string,
	token: string,
): EventSource => {
	// token 走 query（URLSearchParams 自動 encode）
	const query = new URLSearchParams({ token })

	// 建立並回傳 SSE 連線（具名 event 由 caller 自行 addEventListener）
	return new EventSource(
		`${getViewerBaseUrl()}/v1/channels/${channelId}/admin/events?${query.toString()}`,
	)
}
